import { useEffect, useRef, useState } from 'react';

function noteMatchesHighlight(note, highlightTexts) {
    const text = String(note?.text ?? note?.Text ?? '').toLowerCase();
    if (!text) return false;
    return (highlightTexts || []).some(snippet => {
        const q = String(snippet || '').trim().toLowerCase();
        return q && (text === q || text.includes(q) || q.includes(text));
    });
}

/**
 * Yellow sticky-note icon that lists ruleset comments in a click-to-open panel.
 * Renders nothing when there are no notes. Click outside or Escape to close.
 */
export default function RuleSetNotesHover({ notes = [], highlightTexts = [], highlightKey }) {
    const [isOpen, setIsOpen] = useState(false);
    const panelRef = useRef(null);
    const hasHighlights = (highlightTexts || []).length > 0;

    useEffect(() => {
        if (hasHighlights) setIsOpen(true);
    }, [highlightKey, hasHighlights]);

    useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    if (!notes.length) return null;

    return (
        <div
            ref={panelRef}
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
        >
            <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                aria-label="Rule table comments"
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                title="Rule table comments"
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#EAB308',
                    fontSize: '1.15rem'
                }}
            >
                <i className="fa-solid fa-note-sticky"></i>
            </button>
            {isOpen && (
                <div
                    role="dialog"
                    aria-label="Rule table comments"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.5rem',
                        minWidth: '280px',
                        maxWidth: '360px',
                        maxHeight: '280px',
                        overflowY: 'auto',
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        zIndex: 100,
                        padding: '0.75rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem'
                    }}
                >
                    {notes.map((note, index) => {
                        const isHighlighted = noteMatchesHighlight(note, highlightTexts);
                        return (
                            <div
                                key={note.id || index}
                                data-search-hit={isHighlighted ? 'comment' : undefined}
                                style={{
                                    borderBottom: index < notes.length - 1 ? '1px solid var(--border)' : 'none',
                                    paddingBottom: index < notes.length - 1 ? '0.75rem' : 0,
                                    margin: isHighlighted ? '-0.35rem' : 0,
                                    padding: isHighlighted ? '0.5rem 0.45rem 0.75rem' : undefined,
                                    borderRadius: isHighlighted ? '6px' : undefined,
                                    background: isHighlighted ? 'var(--accent-glow)' : undefined,
                                    boxShadow: isHighlighted ? 'inset 0 0 0 1px var(--accent)' : undefined
                                }}
                            >
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', lineHeight: 1.4 }}>
                                    <div>{note.createdBy}</div>
                                    <div>{new Date(note.createdAt).toLocaleString()}</div>
                                </div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                                    {note.text}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
